import {
  createContext,
  useContext,
  type FC,
  useState,
  type SetStateAction,
  type Dispatch,
  type ReactNode,
  useEffect,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useToken } from "@hightouch/htui";
import { isTypingTarget } from "src/components/preview-canvas/is-typing-target";
import { getStoredValue, setStoredValue } from "src/hooks/use-persisted-state";
import { load, save } from "src/utils/storage";
import { hasPlatformModifier } from "src/utils/platform";

export const NAV_EXPANDED_WIDTH = 218;
export const NAV_COLLAPSED_WIDTH = 56;

/** The docked sidebar's narrowest: the width its rows were laid out at. */
export const NAV_MIN_WIDTH = NAV_EXPANDED_WIDTH;
/** The docked sidebar's width until the viewer drags it, a little wider than its narrowest so a typical workspace name shows in full in the header. */
export const NAV_DEFAULT_WIDTH = NAV_MIN_WIDTH + 16;
/** The `xs` size token, which still leaves the page 704px at the 1024px breakpoint where the sidebar can dock. */
export const NAV_MAX_WIDTH = 320;
/** Dragging the sidebar's edge past half its narrowest width collapses it instead. */
export const NAV_COLLAPSE_THRESHOLD = NAV_MIN_WIDTH / 2;

/** Global per user, like the nav's other preferences, rather than per workspace. */
const NAV_WIDTH_KEY = "nav:width";
/** Whether the nav last loaded as the product nav, which collapses out of the window, for the skeleton drawn before React. */
const PRODUCT_NAV_KEY = "nav:product";

export const clampNavWidth = (width: number) =>
  Math.round(Math.min(NAV_MAX_WIDTH, Math.max(NAV_MIN_WIDTH, width)));

/** Whether the nav last loaded as the product nav, for a skeleton drawn before the nav knows. */
export const getSavedIsProductNav = () =>
  getStoredValue<unknown>(PRODUCT_NAV_KEY, false) === true;

export const saveIsProductNav = (isProductNav: boolean) =>
  setStoredValue(PRODUCT_NAV_KEY, isProductNav);

/** The width the viewer last dragged the docked sidebar to; anything stored that isn't a number is ignored. */
export const getSavedNavWidth = () => {
  const stored = getStoredValue<unknown>(NAV_WIDTH_KEY, NAV_DEFAULT_WIDTH);
  return typeof stored === "number" && Number.isFinite(stored)
    ? clampNavWidth(stored)
    : NAV_DEFAULT_WIDTH;
};

type NavWidthType = {
  base: string;
  lg: string;
};

/** Nav open breakpoints. Always closed on mobile. */
export const NAV_OPEN_BREAKPOINT: NavWidthType = {
  base: `${NAV_COLLAPSED_WIDTH}px`,
  lg: `${NAV_EXPANDED_WIDTH}px`,
};

/** Nav closed breakpoints. Always closed on mobile. */
export const NAV_CLOSED_BREAKPOINT: NavWidthType = {
  base: `${NAV_COLLAPSED_WIDTH}px`,
  lg: `${NAV_COLLAPSED_WIDTH}px`,
};

const NAV_HIDDEN_BREAKPOINT: NavWidthType = { base: "0px", lg: "0px" };

export type NavWidthContextType = {
  /** Whether the nav is open or collapsed. */
  isNavOpen: boolean;
  /** Whether the nav can be opened from its collapsed state. Not possible on mobile. */
  canNavOpen: boolean;
  /** The width of the nav. */
  navWidth: NavWidthType;
  /** Set the isNavOpen state. */
  setIsNavOpen: Dispatch<SetStateAction<boolean>>;
  /** Set the isNavOpen state without saving it as the user's preference. A no-op beside the product nav. */
  setIsNavTemporarilyOpen: (isOpen: boolean) => void;
  /** Whether the page has taken the whole window, so no nav renders at all. */
  isNavHidden: boolean;
  setIsNavHidden: (isHidden: boolean) => void;
  /** Whether the collapsed nav is the mini-nav rather than the rail, which takes no width beside the page. */
  isMiniNav: boolean;
  /** Whether this viewer's nav is the mini-nav, which docks at a width they can drag, and collapses out of the way. */
  isMiniNavEligible: boolean;
  setIsMiniNavEligible: (isEligible: boolean) => void;
  /** The mini-nav's docked width, in px, between `NAV_MIN_WIDTH` and `NAV_MAX_WIDTH`. */
  dockedWidth: number;
  /** Sets and saves the docked width. */
  setDockedWidth: (width: number) => void;
};

const defaultContextValue: NavWidthContextType = {
  isNavOpen: true,
  canNavOpen: true,
  navWidth: NAV_OPEN_BREAKPOINT,
  setIsNavOpen: () => {
    throw new Error("useNavWidth must be used within NavWidthProvider");
  },
  setIsNavTemporarilyOpen: () => {
    throw new Error("useNavWidth must be used within NavWidthProvider");
  },
  isNavHidden: false,
  setIsNavHidden: () => {
    throw new Error("useNavWidth must be used within NavWidthProvider");
  },
  isMiniNav: false,
  isMiniNavEligible: false,
  setIsMiniNavEligible: () => {
    throw new Error("useNavWidth must be used within NavWidthProvider");
  },
  dockedWidth: NAV_DEFAULT_WIDTH,
  setDockedWidth: () => {
    throw new Error("useNavWidth must be used within NavWidthProvider");
  },
};

export const NavWidthContext =
  createContext<NavWidthContextType>(defaultContextValue);
NavWidthContext.displayName = "NavWidthContext";

export const useNavWidth = () => {
  const context = useContext(NavWidthContext);
  if (context === defaultContextValue) {
    throw new Error("useNavWidth must be used within NavWidthProvider");
  }
  return context;
};

interface NavWidthProviderProps {
  children: ReactNode;
}

/**
 * Storage key for sidebar preference - global per user (not workspace-specific).
 * Note: The actual localStorage key will be prefixed with "hightouch-" by the storage utility.
 * This key is also referenced in index.html for the skeleton loader (with the prefix).
 */
const SIDEBAR_STORAGE_KEY = "sidebar_collapsed";

/**
 * CSS class added to <html> element to indicate collapsed sidebar state.
 * This is set by the inline script in index.html before React loads (to prevent layout shift),
 * and must be kept in sync with React state to avoid CSS specificity issues.
 */
const SIDEBAR_COLLAPSED_CLASS = "ht-sidebar-collapsed";

/**
 * Get the saved sidebar collapsed preference from storage.
 * Returns undefined if no preference is saved.
 */
function getSavedSidebarCollapsed(): boolean | undefined {
  const saved = load(SIDEBAR_STORAGE_KEY);
  if (saved === null || saved === undefined) return undefined;
  return saved === true;
}

/**
 * Save the sidebar collapsed preference to storage.
 */
function saveSidebarCollapsed(collapsed: boolean): void {
  save(SIDEBAR_STORAGE_KEY, collapsed);
}

/**
 * Get the user's saved open state from localStorage.
 * Returns true (open) if no preference is saved.
 */
export function getSavedIsNavOpen(): boolean {
  const savedCollapsed = getSavedSidebarCollapsed();
  // Default to open (true) if no preference saved
  return savedCollapsed !== true;
}

export const NavWidthProvider: FC<NavWidthProviderProps> = ({ children }) => {
  // Initialize from localStorage immediately to avoid flash
  const [isNavOpen, setIsNavOpenState] = useState(getSavedIsNavOpen);
  const [canNavOpen, setCanNavOpen] = useState(true);
  const [isNavHidden, setIsNavHidden] = useState(false);
  const [isMiniNavEligible, setIsMiniNavEligibleState] = useState(false);
  // Read by the temporary collapse in the same commit the layout publishes eligibility, before state catches up.
  const isMiniNavEligibleRef = useRef(false);
  const setIsMiniNavEligible = useCallback((isEligible: boolean) => {
    isMiniNavEligibleRef.current = isEligible;
    setIsMiniNavEligibleState(isEligible);
  }, []);
  // The product nav folds only by the viewer's hand or a narrowing window, never because a page asked.
  const setIsNavTemporarilyOpen = useCallback((isOpen: boolean) => {
    if (isMiniNavEligibleRef.current) return;
    setIsNavOpenState(isOpen);
  }, []);
  const isMiniNav = isMiniNavEligible && !isNavOpen && !isNavHidden;
  const [dockedWidth, setDockedWidthState] = useState(getSavedNavWidth);
  const setDockedWidth = useCallback((width: number) => {
    const clamped = clampNavWidth(width);
    setDockedWidthState(clamped);
    setStoredValue(NAV_WIDTH_KEY, clamped);
  }, []);
  // Only the mini-nav docks at a width the viewer chose; the classic rail keeps its own.
  const openWidth = isMiniNavEligible ? dockedWidth : NAV_EXPANDED_WIDTH;

  // Track previous canNavOpen to detect mobile -> desktop transitions
  const prevCanNavOpen = useRef(canNavOpen);

  // Wrapper around setIsNavOpen that also persists the preference to localStorage
  const setIsNavOpen: Dispatch<SetStateAction<boolean>> = useCallback(
    (value) => {
      setIsNavOpenState((prev) => {
        const newValue = typeof value === "function" ? value(prev) : value;

        // Persist to localStorage (sidebar_collapsed is the inverse of isNavOpen)
        saveSidebarCollapsed(!newValue);

        return newValue;
      });
    },
    [],
  );

  // Another tab docking, collapsing or resizing the sidebar saves it; this tab follows, where it can dock.
  useEffect(() => {
    const onStorage = () => {
      if (prevCanNavOpen.current) setIsNavOpenState(getSavedIsNavOpen());
      setDockedWidthState(getSavedNavWidth());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // useToken renders the breakpoint in px, we need to remove `px` from the value
  const largeBreakpoint = Number(
    useToken("breakpoints", "lg").replace("px", ""),
  );

  const handleResize = useCallback(() => {
    const isMobile = window.innerWidth < largeBreakpoint;
    const wasMobile = !prevCanNavOpen.current;

    if (isMobile) {
      // On mobile, force collapsed state (but don't save to localStorage)
      setIsNavOpenState(false);
      setCanNavOpen(false);
    } else {
      setCanNavOpen(true);
      // Only restore saved preference when transitioning from mobile to desktop
      if (wasMobile) {
        const savedCollapsed = getSavedSidebarCollapsed();
        setIsNavOpenState(savedCollapsed !== true);
      }
    }

    prevCanNavOpen.current = !isMobile;
  }, [largeBreakpoint]);

  useEffect(() => {
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  // Sync the HTML class with React state to prevent CSS specificity issues.
  // The class is initially set by index.html inline script before React loads.
  useEffect(() => {
    if (isNavOpen) {
      document.documentElement.classList.remove(SIDEBAR_COLLAPSED_CLASS);
    } else {
      document.documentElement.classList.add(SIDEBAR_COLLAPSED_CLASS);
    }
  }, [isNavOpen]);

  // Expose the resolved nav width as a CSS custom property on <html> so
  // portal-rendered components (e.g. the sidebarInset Drawer) can offset by the
  // sidebar without reading React context — useNavWidth() throws outside this
  // provider, and the library Drawer renders in a body-level portal.
  useLayoutEffect(() => {
    const resolvedWidth =
      isNavHidden || isMiniNav
        ? 0
        : canNavOpen && isNavOpen
          ? openWidth
          : NAV_COLLAPSED_WIDTH;
    document.documentElement.style.setProperty(
      "--ht-nav-width",
      `${resolvedWidth}px`,
    );
  }, [isNavOpen, canNavOpen, isNavHidden, isMiniNav, openWidth]);

  // Keyboard shortcut to toggle nav (⌘/ on Mac, Ctrl+/ on Windows)
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      // Check for ⌘/ (Mac) or Ctrl+/ (Windows)
      const isToggleShortcut = event.key === "/" && hasPlatformModifier(event);

      if (!isToggleShortcut) {
        return;
      }

      // Don't trigger when typing in an input or textarea
      const isTyping = isTypingTarget(document.activeElement);

      // A hidden nav has nothing to toggle, and a silent toggle would still change the saved preference.
      // The mini-nav takes the shortcut itself, to fold the page along with the sidebar.
      if (isTyping || isNavHidden || isMiniNavEligible) {
        return;
      }

      event.preventDefault();
      setIsNavOpen((prev) => !prev);
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [setIsNavOpen, isNavHidden, isMiniNavEligible]);

  const navWidth = useMemo(
    () =>
      isNavHidden || isMiniNav
        ? NAV_HIDDEN_BREAKPOINT
        : !isNavOpen
          ? NAV_CLOSED_BREAKPOINT
          : isMiniNavEligible
            ? { base: `${dockedWidth}px`, lg: `${dockedWidth}px` }
            : NAV_OPEN_BREAKPOINT,
    [isNavHidden, isMiniNav, isNavOpen, isMiniNavEligible, dockedWidth],
  );

  const value = useMemo(
    () => ({
      navWidth,
      isNavOpen,
      setIsNavOpen,
      setIsNavTemporarilyOpen,
      canNavOpen,
      isNavHidden,
      setIsNavHidden,
      isMiniNav,
      isMiniNavEligible,
      setIsMiniNavEligible,
      dockedWidth,
      setDockedWidth,
    }),
    [
      navWidth,
      isNavOpen,
      setIsNavOpen,
      setIsNavTemporarilyOpen,
      canNavOpen,
      isNavHidden,
      isMiniNav,
      isMiniNavEligible,
      setIsMiniNavEligible,
      dockedWidth,
      setDockedWidth,
    ],
  );

  return (
    <NavWidthContext.Provider value={value}>
      {children}
    </NavWidthContext.Provider>
  );
};

/**
 * Hook to collapse the nav on mount.
 * Useful for pages that need more horizontal space (e.g., canvas/graph views).
 */
export function useCollapsedNav() {
  const { setIsNavOpen } = useNavWidth();

  useEffect(() => {
    setIsNavOpen(false);
  }, [setIsNavOpen]);
}
